import React from "react";
import { View } from "react-native";
import { AppText } from "@/components/ui/AppText";
import type { BusinessInformation } from "@/lib/api/review";
import { DetailField, FieldEditedBadge, type FieldEditInfo } from "./Shared";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import { FilePreview } from "@/components/ui/FilePreview";

interface BusinessInformationTabProps {
  bizInfo: BusinessInformation | undefined;
  fieldEdits?: Record<string, FieldEditInfo>;
  onShowFieldEdit?: (fieldKey: string, titleKey: TranslationKeys) => void;
  t: ReturnType<typeof import("@/context/I18nContext").useI18n>["t"];
  applicationId: string;
}

export function BusinessInformationTab({
  bizInfo,
  fieldEdits,
  onShowFieldEdit,
  applicationId,
  t,
}: BusinessInformationTabProps) {
  return (
    <View className="max-w-4xl items-start">
      <AppText weight="bold" className="text-xl text-dark-text-50 mb-2">
        {t("review.application.businessInformation.title")}
      </AppText>
      <AppText className="text-base text-dark-text-20 mb-8 leading-6">
        {t("review.application.businessInformation.description")}
      </AppText>

      <View className="h-px bg-stroke-20 mb-8 w-full" />

      {/* Company Legal Name (Chinese) — full width */}
      <View className="flex-row w-full">
        <View className="flex-1">
          <DetailField
            label={t("review.application.businessInformation.fields.companyNameChinese")}
          >
            <AppText weight="medium" className="text-base text-dark-text-50">
              {bizInfo?.companyNameChinese}
            </AppText>
          </DetailField>
        </View>
      </View>

      {/* Company Legal Name (English) + (Arabic) */}
      <View className="flex-col tablet:flex-row w-full">
        <View className="tablet:flex-1 tablet:pe-6">
          <DetailField
            label={t("review.application.businessInformation.fields.companyNameEnglish")}
          >
            <AppText weight="medium" className="text-base text-dark-text-50">
              {bizInfo?.companyNameEnglish}
            </AppText>
          </DetailField>
        </View>
        <View className="tablet:flex-1 tablet:ps-6">
          <DetailField label={t("review.application.businessInformation.fields.companyNameArabic")}>
            {bizInfo?.companyNameArabic ? (
              <AppText weight="medium" className="text-base text-dark-text-50">
                {bizInfo.companyNameArabic}
              </AppText>
            ) : (
              <AppText className="text-base text-dark-text-20 italic">
                {t("review.application.businessInformation.notProvided")}
              </AppText>
            )}
            {fieldEdits?.["companyNameArabic"] && onShowFieldEdit && (
              <FieldEditedBadge
                onShowEdit={() =>
                  onShowFieldEdit(
                    "companyNameArabic",
                    "review.application.businessInformation.fields.companyNameArabic"
                  )
                }
                t={t}
              />
            )}
          </DetailField>
        </View>
      </View>

      {/* Registered Office Address — full width */}
      <View className="flex-row w-full">
        <View className="flex-1">
          <DetailField label={t("review.application.businessInformation.fields.registeredAddress")}>
            <AppText weight="medium" className="text-base text-dark-text-50">
              {bizInfo?.registeredAddress}
            </AppText>
          </DetailField>
        </View>
      </View>

      {/* City + Postal Code */}
      <View className="flex-col tablet:flex-row w-full">
        <View className="tablet:flex-1 tablet:pe-6">
          <DetailField label={t("review.application.businessInformation.fields.city")}>
            <AppText weight="medium" className="text-base text-dark-text-50">
              {bizInfo?.city}
            </AppText>
          </DetailField>
        </View>
        <View className="tablet:flex-1 tablet:ps-6">
          <DetailField label={t("review.application.businessInformation.fields.postalCode")}>
            <AppText weight="medium" className="text-base text-dark-text-50">
              {bizInfo?.postalCode}
            </AppText>
          </DetailField>
        </View>
      </View>

      {/* USSC */}
      <View className="flex-col w-full">
        <View className="tablet:flex-1 tablet:pe-6">
          <DetailField label={t("review.application.businessInformation.fields.ussc")}>
            <AppText weight="medium" className="text-base text-dark-text-50">
              {bizInfo?.ussc}
            </AppText>
          </DetailField>
        </View>
      </View>

      {/* Business License */}
      <View className="flex-col w-full">
        <DetailField label={t("review.application.businessInformation.fields.businessLicense")}>
          {bizInfo?.businessLicenseUrl ? (
            <FilePreview
              className="max-w-[320px]"
              fileKey={bizInfo?.businessLicenseUrl}
              isVendor={false}
              applicationId={applicationId}
            />
          ) : (
            <AppText className="text-sm text-dark-text-20 text-center">
              {t("review.application.legalRepresentative.notUploaded")}
            </AppText>
          )}
        </DetailField>
      </View>
    </View>
  );
}
